import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function Wallet() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('app');

  const [packages, setPackages] = useState([]);
  const [packageStatus, setPackageStatus] = useState(null);
  const [creditBalance, setCreditBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [selections, setSelections] = useState({});
  const [expandedItems, setExpandedItems] = useState({});

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [authLoading, user, navigate]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const packagesRes = await fetch(`${process.env.REACT_APP_API_URL}/api/packages`);
      if (!packagesRes.ok) throw new Error('Failed to fetch packages');
      const packagesData = await packagesRes.json();
      setPackages(packagesData.packages || []);

      const statusRes = await fetch(`${process.env.REACT_APP_API_URL}/api/user/package-status`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!statusRes.ok) throw new Error('Failed to fetch package status');
      const statusData = await statusRes.json();
      setPackageStatus(statusData);

      const balanceRes = await fetch(`${process.env.REACT_APP_API_URL}/api/user/credit-balance`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!balanceRes.ok) throw new Error('Failed to fetch credit balance');
      const balanceData = await balanceRes.json();
      setCreditBalance(balanceData.credit_point_balance || 0);
    } catch (err) {
      console.error('Failed to fetch wallet data:', err);
      setError('Failed to load packages. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  const toggleItemExpand = (packageCode, itemIndex) => {
    const key = `${packageCode}-${itemIndex}`;
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handlePurchase = async (packageId) => {
    if (!user) {
      navigate('/login');
      return;
    }
    setPurchaseError(null);
    setSuccessMessage(null);
    setPurchasing(true);

    try {
      const token = localStorage.getItem('token');
      const selected_item_ids = selections[packageId] ? Object.values(selections[packageId]) : [];

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/purchase-package`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          package_id: packageId,
          selected_item_ids: selected_item_ids
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.error === 'insufficient_credits') {
          setPurchaseError(t('packagePurchase.insufficientError'));
        } else if (data.error === 'package_already_active') {
          setPurchaseError(t('packagePurchase.alreadyActive'));
        } else if (data.error === 'downgrade_not_allowed') {
          setPurchaseError('Cannot downgrade to a lower-priced package');
        } else if (data.error === 'selection_required') {
          setPurchaseError(t('packagePurchase.selectionRequired'));
        } else if (data.error === 'invalid_selection') {
          setPurchaseError(t('packagePurchase.invalidSelection'));
        } else if (data.error === 'invalid_package_config') {
          setPurchaseError(t('packagePurchase.invalidPackageConfig'));
        } else {
          setPurchaseError(data.message || t('packagePurchase.failedToSubmit'));
        }
        return;
      }

      const pkg = packages.find(p => p.id === packageId);
      setSuccessMessage(t('packagePurchase.purchaseSuccess', { price: pkg.price, balance: data.new_balance }));
      setCreditBalance(data.new_balance);
      await fetchData();
    } catch (err) {
      console.error('Purchase error:', err);
      setPurchaseError(t('packagePurchase.failedToSubmit'));
    } finally {
      setPurchasing(false);
    }
  };

  const hasAllSelectionsForPackage = (pkg) => {
    if (!pkg.items || pkg.items.length === 0) return true;
    for (const item of pkg.items) {
      if (item.options.length > 1) {
        if (!selections[pkg.id]?.[item.slot_number]) return false;
      }
    }
    return true;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-gray-600">{t('common.loading')}</p>
      </div>
    );
  }

  const isVerified = packageStatus?.verified;
  const currentPackage = packageStatus?.package;

  // Ensure all packages have series (default to 'Y' for legacy packages)
  const packagesWithSeries = packages.map(pkg => ({
    ...pkg,
    series: pkg.series || 'Y',
    description_en: pkg.description_en || pkg.name_en || '',
    description_cn: pkg.description_cn || pkg.name_cn || ''
  }));

  // Repurchase packages: combine all series, filter by >= price, sort by price
  const repurchasePackages = isVerified
    ? packagesWithSeries
        .filter(pkg => pkg.price >= currentPackage?.price)
        .sort((a, b) => a.price - b.price)
    : [];

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <style>{`
        .spacing-xs { margin: 4px; }
        .spacing-sm { margin: 8px; }
        .spacing-md { margin: 16px; }
        .spacing-lg { margin: 24px; }
        .spacing-xl { margin: 32px; }

        button { min-height: 48px; }

        .category-scroll {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          scroll-behavior: smooth;
          -webkit-overflow-scrolling: touch;
          padding-left: 16px;
          padding-right: 16px;
          margin: 16px 0;
        }

        .category-scroll::-webkit-scrollbar {
          height: 4px;
        }

        .category-scroll::-webkit-scrollbar-thumb {
          background-color: rgba(0, 0, 0, 0.2);
          border-radius: 2px;
        }

        .product-card {
          animation: fadeIn 0.3s ease-in;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (max-width: 430px) {
          .products-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 16px;
            padding: 16px;
          }

          .header-sticky {
            position: sticky;
            top: 0;
            z-index: 20;
            background: white;
            border-bottom: 1px solid #e5e7eb;
            height: 56px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0 16px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          }

          .hero-mobile {
            height: 240px;
            background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
            padding: 24px 16px;
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
            color: white;
          }

          .hero-title {
            font-size: 28px;
            font-weight: 700;
            line-height: 1.2;
            margin-bottom: 8px;
          }

          .hero-subtitle {
            font-size: 14px;
            font-weight: 400;
            line-height: 1.5;
            margin-bottom: 16px;
            opacity: 0.95;
          }

          .product-card {
            background: white;
            border-radius: 16px;
            overflow: hidden;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
            transition: all 0.3s ease;
          }

          .product-card:active {
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
          }

          .product-image {
            width: 100%;
            aspect-ratio: 1 / 1;
            background: #f3f4f6;
            object-fit: contain;
            padding: 16px;
          }

          .product-content {
            padding: 16px;
          }

          .product-code {
            font-size: 12px;
            font-family: monospace;
            color: #999;
            margin-bottom: 4px;
          }

          .product-name {
            font-size: 20px;
            font-weight: 600;
            color: #111;
            margin-bottom: 6px;
            line-height: 1.2;
          }

          .product-description {
            font-size: 14px;
            color: #666;
            margin-bottom: 12px;
            line-height: 1.4;
          }

          .price-badge {
            background: #f0f4ff;
            border-left: 4px solid #2563eb;
            padding: 8px 12px;
            margin-bottom: 12px;
            border-radius: 4px;
          }

          .price-value {
            font-size: 28px;
            font-weight: 700;
            color: #2563eb;
            line-height: 1;
          }

          .price-label {
            font-size: 12px;
            color: #666;
            margin-top: 4px;
          }

          .bonus-badge {
            background: #ecfdf5;
            border-left: 4px solid #10b981;
            padding: 8px 12px;
            margin-bottom: 12px;
            border-radius: 4px;
          }

          .bonus-title {
            font-size: 12px;
            font-weight: 600;
            color: #059669;
            margin-bottom: 4px;
          }

          .bonus-item {
            font-size: 14px;
            color: #047857;
            margin: 4px 0;
          }

          .accordion-item {
            border-top: 1px solid #e5e7eb;
            padding: 12px 0;
          }

          .accordion-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            font-size: 14px;
            font-weight: 500;
            color: #111;
            cursor: pointer;
            padding: 8px 0;
            background: none;
            border: none;
            width: 100%;
          }

          .accordion-header:active {
            opacity: 0.7;
          }

          .accordion-icon {
            font-size: 18px;
            transition: transform 0.3s ease;
          }

          .accordion-icon.expanded {
            transform: rotate(180deg);
          }

          .accordion-content {
            font-size: 13px;
            color: #666;
            line-height: 1.6;
            max-height: 0;
            overflow: hidden;
            transition: max-height 0.3s ease;
          }

          .accordion-content.expanded {
            max-height: 500px;
            margin-top: 8px;
          }

          .cta-button {
            width: 100%;
            height: 48px;
            background: #2563eb;
            color: white;
            border: none;
            border-radius: 12px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            margin-top: 8px;
          }

          .cta-button:active {
            background: #1d4ed8;
            transform: scale(0.98);
          }

          .cta-button:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          .status-badge {
            background: white;
            border-radius: 12px;
            padding: 16px;
            margin: 16px;
            border-left: 4px solid #10b981;
          }

          .badge-label {
            display: inline-block;
            font-size: 11px;
            font-weight: 600;
            padding: 4px 8px;
            margin: 4px 0 8px 0;
            border-radius: 4px;
          }

          .badge-current-tier {
            background: #dbeafe;
            color: #1e40af;
          }

          .badge-upgrade {
            background: #dbeafe;
            color: #1e40af;
          }

          .credit-balance-card {
            background: white;
            border-radius: 12px;
            padding: 16px;
            margin: 16px;
            border-left: 4px solid #f59e0b;
            position: sticky;
            top: 56px;
            z-index: 10;
          }
        }

        @media (min-width: 431px) {
          .products-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
            gap: 24px;
            padding: 32px;
          }

          .credit-balance-card {
            position: sticky;
            top: 56px;
            z-index: 10;
          }
        }
      `}</style>

      {/* Sticky Mobile Header */}
      <div className="header-sticky">
        <h1 className="text-xl font-bold text-gray-900">Wellis</h1>
      </div>

      {/* Credit Balance (sticky) */}
      <div className="credit-balance-card">
        <p className="text-sm text-gray-600 mb-1">Available Credit Balance (SP)</p>
        <p className="text-2xl font-bold text-amber-600">{creditBalance.toLocaleString()}</p>
      </div>

      {/* Unverified State */}
      {!isVerified && (
        <div className="text-center py-16 px-4">
          <div className="inline-block mb-6">
            <span className="text-5xl">🎁</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Get Your First Package</h2>
          <p className="text-gray-600 mb-8">Purchase your initial package to become a verified member and unlock all benefits</p>
          <button
            onClick={() => navigate('/packages')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Buy Your First Package
          </button>
        </div>
      )}

      {/* Repurchase Grid (verified members only) */}
      {isVerified && (
        <>
          <div className="hero-mobile">
            <h2 className="hero-title">{i18n.language === 'zh' ? '升级或续购您的套餐' : 'Repurchase or Upgrade'}</h2>
            <p className="hero-subtitle">{i18n.language === 'zh' ? '选择套餐以添加新的位置或升级到更高等级' : 'Choose a package to add more positions or upgrade to a higher tier'}</p>
          </div>

          {repurchasePackages.length > 0 ? (
            <div className="products-grid">
              {repurchasePackages.map((pkg) => {
                const canAfford = creditBalance >= pkg.price;
                const isCurrentTier = pkg.price === currentPackage?.price;
                const isUpgrade = pkg.price > currentPackage?.price;

                return (
                  <div key={pkg.id} className="product-card">
                    {/* Product Image */}
                    <div className="relative">
                      {pkg.image_url ? (
                        <img
                          src={pkg.image_url}
                          alt={pkg.code}
                          className="product-image"
                          loading="lazy"
                        />
                      ) : (
                        <div className="product-image flex items-center justify-center">
                          <span className="text-gray-400">{pkg.code}</span>
                        </div>
                      )}
                    </div>

                    {/* Product Info */}
                    <div className="product-content">
                      <p className="product-code">{pkg.code}</p>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="product-name">
                          {i18n.language === 'zh' ? pkg.name_cn : pkg.name_en}
                        </h3>
                        <span style={{ fontSize: '11px', fontWeight: '600', padding: '4px 8px', borderRadius: '4px', backgroundColor: '#dbeafe', color: '#1e40af' }}>
                          {isUpgrade ? 'Upgrade' : 'Current Tier'}
                        </span>
                      </div>
                      <p className="product-description">
                        {i18n.language === 'zh' ? pkg.description_cn : pkg.description_en}
                      </p>

                      {/* Price Badge */}
                      <div className="price-badge">
                        <div className="price-value">
                          {pkg.price.toLocaleString()}
                        </div>
                        <div className="price-label">
                          {i18n.language === 'zh' ? '认购积分 (SP)' : 'Credit Points (SP)'}
                        </div>
                      </div>

                      {/* Welcome Bonus */}
                      {(pkg.cash_amount > 0 || pkg.product_credit > 0) && (
                        <div className="bonus-badge">
                          <div className="bonus-title">
                            {i18n.language === 'zh' ? '欢迎奖励' : 'Welcome Bonus'}
                          </div>
                          {pkg.cash_amount > 0 && (
                            <div className="bonus-item">
                              {pkg.cash_amount} {i18n.language === 'zh' ? '现金积分 (CP)' : 'Cash Points (CP)'}
                            </div>
                          )}
                          {pkg.product_credit > 0 && (
                            <div className="bonus-item">
                              {pkg.product_credit} {i18n.language === 'zh' ? '产品积分 (PP)' : 'Product Points (PP)'}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Accordion - What's Included */}
                      {pkg.items && pkg.items.length > 0 && (
                        <div className="mt-4">
                          {pkg.items.map((item, idx) => {
                            const itemKey = `${pkg.id}-${idx}`;
                            const isExpanded = expandedItems[itemKey];

                            return (
                              <div key={idx} className="accordion-item">
                                <button
                                  className="accordion-header"
                                  onClick={() => toggleItemExpand(pkg.id, idx)}
                                >
                                  <span>{i18n.language === 'zh' ? item.options?.[0]?.option_label_cn : item.options?.[0]?.option_label_en}</span>
                                  <span className={`accordion-icon ${isExpanded ? 'expanded' : ''}`}>
                                    ▼
                                  </span>
                                </button>
                                <div className={`accordion-content ${isExpanded ? 'expanded' : ''}`}>
                                  {item.options?.map((opt) => (
                                    <div key={opt.id} className="mb-2">
                                      <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                          type="radio"
                                          name={`slot_${pkg.id}_${item.slot_number}`}
                                          value={opt.id}
                                          checked={selections[pkg.id]?.[item.slot_number] === opt.id}
                                          onChange={() => {
                                            setSelections(prev => ({
                                              ...prev,
                                              [pkg.id]: {
                                                ...prev[pkg.id],
                                                [item.slot_number]: opt.id
                                              }
                                            }));
                                          }}
                                        />
                                        <span className="text-sm">
                                          {i18n.language === 'zh' ? opt.option_label_cn : opt.option_label_en}
                                        </span>
                                      </label>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* CTA Button */}
                      <button
                        onClick={() => handlePurchase(pkg.id)}
                        disabled={!canAfford || purchasing || !hasAllSelectionsForPackage(pkg)}
                        className="cta-button"
                        title={!canAfford ? 'Insufficient Credits' : !hasAllSelectionsForPackage(pkg) ? 'Complete selections' : ''}
                      >
                        {!canAfford ? `Need ${(pkg.price - creditBalance).toFixed(0)} more` : !hasAllSelectionsForPackage(pkg) ? 'Select items' : purchasing ? 'Processing...' : isCurrentTier ? 'Repurchase' : 'Upgrade'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 px-4">
              <p className="text-gray-600 text-lg">
                {i18n.language === 'zh' ? '没有可用的套餐' : 'No available packages'}
              </p>
            </div>
          )}
        </>
      )}

      {/* Error/Success Messages */}
      {error && <div className="bg-red-50 border-l-4 border-red-500 p-4 mx-4 rounded text-red-700 text-sm">{error}</div>}
      {purchaseError && <div className="bg-red-50 border-l-4 border-red-500 p-4 mx-4 rounded text-red-700 text-sm">{purchaseError}</div>}
      {successMessage && <div className="bg-green-50 border-l-4 border-green-500 p-4 mx-4 rounded text-green-700 text-sm">{successMessage}</div>}
    </div>
  );
}
